import { ReactElement } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import cn from 'classnames';
import { ReviewIndicator, ReviewIndicatorType } from '../media-inputs-utils';

const INDICATOR_LABEL: Record<ReviewIndicatorType, string> = {
  [ReviewIndicator.INVESTIGATE]: 'Needs investigating',
  [ReviewIndicator.NOTE]: 'Worth noting',
  [ReviewIndicator.NO_BPM]: 'No BPM comparison',
  [ReviewIndicator.CLEAR]: 'No issues found'
};

const INDICATOR_CLASS: Record<ReviewIndicatorType, string> = {
  [ReviewIndicator.INVESTIGATE]: 'investigate',
  [ReviewIndicator.NOTE]: 'note',
  [ReviewIndicator.NO_BPM]: 'no-bpm',
  [ReviewIndicator.CLEAR]: 'clear'
};

interface IndicatorDotProps {
  indicator: ReviewIndicatorType;
  // Why it's this color (e.g. "Low spend-vs-impressions correlation (0.52)"), shown after
  // the indicator's meaning in the hover text.
  reasons?: string[];
  // False when the dot sits on a pill or tab that carries the tooltip itself (see
  // IndicatorTooltip), so the hover target isn't just the 10px dot. The dot then has no
  // hover text of its own (only its aria-label), so wrap its parent in IndicatorTooltip.
  tooltip?: boolean;
}

// The colored review-status dot on a pill, tab or row. Hovering it says what it means and
// why: "Worth noting — Low spend-vs-impressions correlation (0.52)".
export const IndicatorDot = ({ indicator, reasons = [], tooltip = true }: IndicatorDotProps) => {
  const label = INDICATOR_LABEL[indicator];
  const dot = (
    <span
      className={cn(
        'data-qa-indicator-dot',
        `data-qa-indicator-dot--${INDICATOR_CLASS[indicator]}`
      )}
      role='img'
      aria-label={reasons.length > 0 ? `${label}: ${reasons.join('; ')}` : label}
    />
  );
  if (!tooltip) return dot;

  return (
    <IndicatorTooltip indicator={indicator} reasons={reasons}>
      {dot}
    </IndicatorTooltip>
  );
};

interface IndicatorTooltipProps {
  indicator: ReviewIndicatorType;
  reasons?: string[];
  // A pill row hovered end to end shouldn't pop "No issues found" over every clean pill.
  hideWhenClear?: boolean;
  children: ReactElement;
}

// The indicator's hover text on whatever it wraps: the dot itself, or a whole pill or tab.
export const IndicatorTooltip = ({
  indicator,
  reasons = [],
  hideWhenClear = false,
  children
}: IndicatorTooltipProps) => {
  if (hideWhenClear && indicator === ReviewIndicator.CLEAR) return children;

  return (
    <OverlayTrigger
      placement='top'
      overlay={
        <Tooltip className='data-qa-indicator-tooltip'>
          <IndicatorTooltipBody label={INDICATOR_LABEL[indicator]} reasons={reasons} />
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  );
};

interface IndicatorTooltipBodyProps {
  label: string;
  reasons: string[];
}

// One reason reads as a sentence; several as a list under the label.
const IndicatorTooltipBody = ({ label, reasons }: IndicatorTooltipBodyProps) => {
  if (reasons.length === 0) return <strong>{label}</strong>;
  if (reasons.length === 1) {
    return (
      <>
        <strong>{label}</strong> — {reasons[0]}
      </>
    );
  }
  return (
    <>
      <strong>{label}</strong>
      <ul>
        {reasons.map(reason => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </>
  );
};
